// src/features/status/statusLabels.ts
//
// Every source kind, delivery class and status the API sends
// (V2SourceKind / V2SourceDeliveryClass / V2SourceStatus) mapped to a neutral,
// translated label — internal producer names never reach the screen. Anything
// new or unknown falls back to a generic label.

export type ChipColor = 'success' | 'warning' | 'error' | 'default';

const KINDS = ['Kiosk', 'Gateway', 'Oci', 'BackOffice', 'DispenserShim', 'Partner', 'Locks', 'Assistant'] as const;
const DELIVERY = ['Durable', 'Lossy'] as const;

const STATUS_COLOR: Record<string, ChipColor> = {
  Active: 'success',
  Suspended: 'warning',
  Revoked: 'error',
};

export const sourceKindKey = (kind: string): string =>
  (KINDS as readonly string[]).includes(kind) ? `resources.status.kinds.${kind}` : 'resources.status.kinds.other';

export const deliveryClassKey = (value: string): string =>
  (DELIVERY as readonly string[]).includes(value) ? `resources.status.delivery.${value}` : 'resources.status.delivery.other';

export const sourceStatusKey = (value: string): string =>
  value in STATUS_COLOR ? `resources.status.states.${value}` : 'resources.status.states.other';

export const sourceStatusColor = (value: string): ChipColor => STATUS_COLOR[value] ?? 'default';

/**
 * Status asks for the last 48 hours ending now until the person picks a
 * period; a picked period covers whole local days, the last one up to now.
 */
export const statusWindow = (
  period: { from: string; to: string } | null,
  now: Date = new Date()
): { from: string; to: string } => {
  if (!period) {
    return { from: new Date(now.getTime() - 48 * 3600 * 1000).toISOString(), to: now.toISOString() };
  }
  const from = new Date(`${period.from}T00:00:00`);
  const end = new Date(`${period.to}T23:59:59.999`);
  return { from: from.toISOString(), to: (end > now ? now : end).toISOString() };
};
